import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogIn, Layers, Sparkles, Calendar as CalendarIcon, BarChart3, Mail, Lock, User, Loader2, Gift, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/utils/constants";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const LoginPage = ({ onAuthSuccess }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = mode === "register" ? "register" : "login";
      const payload = mode === "register"
        ? { name: form.name, email: form.email, password: form.password, referral_code: localStorage.getItem('opero_ref') || null }
        : { email: form.email, password: form.password };
      const { data } = await axios.post(`${API}/auth/${endpoint}`, payload, { withCredentials: true });
      if (data.user) {
        if (mode === "register") localStorage.removeItem('opero_ref');
        toast.success(mode === "register" ? "Welcome to Opero Smart!" : "Welcome back!");
        if (onAuthSuccess) onAuthSuccess(data.user);
        else window.location.reload();
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1688169445191-c69c4173ac81?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="w-full max-w-md relative z-10">
        <div className="bg-card rounded-2xl shadow-xl border p-8">
          <div className="text-center">
            <h1 className="font-heading text-4xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent mb-2">Opero Smart</h1>
            <p className="text-muted-foreground mb-6">Your productivity, simplified</p>
          </div>

          {localStorage.getItem('opero_pending_invite') && (
            <div className="flex items-center gap-2.5 rounded-xl border border-sky-500/30 bg-sky-500/5 px-4 py-3 mb-5 text-left" data-testid="team-invite-banner">
              <Users className="h-4 w-4 text-sky-500 shrink-0" />
              <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">Team invitation pending.</span> Sign in or create a free account with the invited email to join the team.</p>
            </div>
          )}
          {localStorage.getItem('opero_ref') && (
            <div className="flex items-center gap-2.5 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 mb-5 text-left" data-testid="referral-invite-banner">
              <Gift className="h-4 w-4 text-primary shrink-0" />
              <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">You've been invited!</span> Sign up free and the friend who invited you gets a month of Pro.</p>
            </div>
          )}

          <Button onClick={handleGoogleLogin} variant="outline" className="w-full h-11" data-testid="login-btn">
            <LogIn className="h-5 w-5 mr-2" />Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or with email</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input required placeholder="Your name" className="pl-9" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name-input" />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input required type="email" placeholder="Email address" className="pl-9" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input" />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input required type="password" placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"} minLength={mode === "register" ? 8 : undefined} className="pl-9" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password-input" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-11 bg-gradient-to-r from-primary to-purple-600 hover:opacity-90" data-testid="auth-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "register" ? "Create account" : "Sign in"}
            </Button>
          </form>

          <p className="text-sm text-center text-muted-foreground mt-4">
            {mode === "login" ? (
              <>No account?{" "}<button type="button" className="text-primary font-medium hover:underline" onClick={() => { setMode("register"); setError(""); }} data-testid="switch-to-register">Sign up free</button></>
            ) : (
              <>Already have an account?{" "}<button type="button" className="text-primary font-medium hover:underline" onClick={() => { setMode("login"); setError(""); }} data-testid="switch-to-login">Sign in</button></>
            )}
          </p>

          <p className="text-xs text-muted-foreground text-center mt-3">By continuing, you agree to our Terms of Service and Privacy Policy</p>

          <div className="mt-6 pt-5 border-t">
            <div className="grid grid-cols-2 gap-3 text-left">
              <div className="p-3 bg-muted/50 rounded-lg"><Layers className="h-5 w-5 text-primary mb-1" /><span className="text-sm">Kanban Boards</span></div>
              <div className="p-3 bg-muted/50 rounded-lg"><Sparkles className="h-5 w-5 text-amber-500 mb-1" /><span className="text-sm">AI Assistant</span></div>
              <div className="p-3 bg-muted/50 rounded-lg"><CalendarIcon className="h-5 w-5 text-blue-500 mb-1" /><span className="text-sm">Calendar View</span></div>
              <div className="p-3 bg-muted/50 rounded-lg"><BarChart3 className="h-5 w-5 text-emerald-500 mb-1" /><span className="text-sm">Analytics</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AuthCallback = ({ onAuthSuccess }) => {
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      if (!sessionIdMatch) { window.location.href = '/'; return; }
      const sessionId = sessionIdMatch[1];
      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        if (response.data.user) { window.history.replaceState({}, '', '/'); onAuthSuccess(response.data.user); }
        else throw new Error('No user data');
      } catch (error) {
        console.error('Auth error:', error);
        toast.error('Authentication failed');
        window.history.replaceState({}, '', '/');
        window.location.reload();
      }
    };
    processAuth();
  }, [onAuthSuccess]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <h2 className="text-xl font-semibold">Signing in...</h2>
      </div>
    </div>
  );
};
